import type { LifecycleConfig } from 'features/libra/lifecycle/lifecycleTypes'
import type {
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import type {
  SignoffConfig,
  SignoffsField,
} from 'features/libra/signoff/signoffTypes'
import type { NiiReportFields } from 'features/libra/stepComponents/niiReport/niiReportApi'
import type { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type { EligibilityStatus } from 'features/libra/types/eligibility'
import type { LibraWorkflowStep } from 'features/libra/workflow/workflowStepsApi'
import type { PdfTableData } from 'features/reporting/pdfGeneration/components/shared/PdfTable'

import type { NiiArrayTableBlock } from './buildNiiArrayTableBlocks'
import type { NiiFormSectionBlock } from './buildNiiFormSectionBlocks'
import type { NiiZoneAnalysis } from './buildNiiZoneAnalysisContent'

/** A value overridden by hand, as display text. */
export interface NiiOverrideDetail {
  /** What was overridden, e.g. "Total surface area". */
  label: string
  calculated?: string
  value: string
  justification?: string
  by?: string
  at?: string
}

/** "who, when" for an override, or blank when neither was recorded. */
export function formatOverriddenBy(override: NiiOverrideDetail): string {
  return [override.by, override.at].filter(Boolean).join(', ')
}

/** Shared chrome always rendered at the top of the PDF (not a workflow step). */
export interface NiiReportMeta {
  siteName?: string
  assetName?: string
  analysisDate?: string
  analysisVersion: number
}

export interface NiiZoneImage {
  name: string
  dataUrl: string
}

export interface NiiQuestionList {
  title?: string
  answers: ReadonlyArray<{ question: string; answer: boolean }>
  override?: NiiOverrideDetail
}

export interface NiiZoneInventoryEntry {
  title: string
  color: string
  zoneIndex: number
  description?: string
  designAndFunction?: PdfTableData
  damageMechanisms?: PdfTableData
}

export interface NiiReportCloseout {
  executiveSummary?: string
  internalComments?: string
  eligibility: EligibilityStatus
  recommendations: ReadonlyArray<string>
  justification?: string
  screenedOutAtPreScreening?: boolean
  zoneRecommendations: ReadonlyArray<{
    title: string
    color: string
    zone_recommendations: ReadonlyArray<string>
  }>
}

/**
 * One sign-off slot at export time. `signedBy` and `signedAt` are present only
 * while the slot holds a signature that currently counts — a reset leaves them
 * absent even though its history is kept on the ticket.
 */
export interface NiiSignoffEntry {
  slotId: string
  title: string
  signedBy?: string
  /** ISO timestamp. */
  signedAt?: string
  /** Shown in place of the signer when unsigned: pending or not required. */
  missingText: string
}

/**
 * One step of the review — submission or approval — as recorded on the
 * ticket. `by` and `at` are absent when the step hasn't happened yet, or
 * happened before it was recorded; `missingText` says which.
 */
export interface NiiReviewMilestone {
  label: string
  by?: string
  /** ISO timestamp. */
  at?: string
  missingText: string
}

/** Where the ticket sits in its lifecycle, as recorded on the ticket. */
export interface NiiTicketLifecycle {
  config: LifecycleConfig | undefined
  ticketStatus: string
  terminalTransitionBy: string | null
  terminalTransitionAt: string | null
  submittedForReviewBy: string | null
  submittedForReviewAt: string | null
}

/**
 * One PDF block per exported workflow step.
 * Content is filled by the component registry from that step's config.
 */
export interface NiiReportSection {
  stepId: string
  title: string
  formBlocks?: ReadonlyArray<NiiFormSectionBlock>
  arrayTables?: ReadonlyArray<NiiArrayTableBlock>
  questionLists?: ReadonlyArray<NiiQuestionList>
  zoneInventory?: ReadonlyArray<NiiZoneInventoryEntry>
  zoneAnalysis?: NiiZoneAnalysis
  zoneImages?: ReadonlyArray<NiiZoneImage>
  reportCloseout?: NiiReportCloseout
  /**
   * Only on the closing review section, after the sign-offs: submission then
   * approval, each present only when the workflow's lifecycle has that step.
   */
  reviewMilestones?: ReadonlyArray<NiiReviewMilestone>
  /** Only on the closing review section, in configured slot order. */
  signoff?: ReadonlyArray<NiiSignoffEntry>
  /**
   * Set on the review section when the ticket was approved before its
   * workflow required sign-off, so no slot was ever signed.
   */
  approvedBeforeSignoffRequired?: boolean
}

/**
 * Ticket-wide inputs for the step builders. Anything configured per step
 * (zone fields, confidence, CoF, …) is resolved by that step's builder from
 * `fieldValues` + the step's own config, not carried here.
 */
export interface NiiReportContentInputs {
  fieldValues: FieldValues
  ontologyFields: Record<string, OntologyField>
  /** Zones for the report step's own zoneFieldName (recommendations, images). */
  zones: ReadonlyArray<ZoneParams>
  zoneImages: ReadonlyArray<NiiZoneImage>
  reportFields: NiiReportFields
  /**
   * Required rather than optional so no export path can silently drop the
   * sign-off table; pass `undefined` for a workflow without sign-off.
   */
  signoffConfig: SignoffConfig | undefined
  /** The ticket's sign-off entry state; `{}` when nothing is signed. */
  signoffs: SignoffsField
  /** Required for the same reason; decides the review milestones and `approvedBeforeSignoffRequired`. */
  lifecycle: NiiTicketLifecycle | undefined
}

/**
 * Export in workflow order, using the full workflow rather than the review
 * sequence, which can omit steps such as Data Gathering.
 */
export function resolveNiiReportSections(
  workflowSteps: ReadonlyArray<
    Pick<LibraWorkflowStep, 'id' | 'name' | 'component' | 'config'>
  >,
  pdfComponents: ReadonlySet<string>
): ReadonlyArray<
  Pick<LibraWorkflowStep, 'id' | 'name' | 'component' | 'config'>
> {
  return workflowSteps.filter(
    (step) =>
      step.component !== 'NiiReview' && pdfComponents.has(step.component)
  )
}
